import { useState, useMemo, useRef } from "react";
import { Select, InputNumber, Input, Tooltip, Skeleton, Empty, Modal } from "antd";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined, SaveOutlined,
  SearchOutlined, ApartmentOutlined, DownloadOutlined, UploadOutlined, RightOutlined,
} from "@ant-design/icons";
import { exportLeadAssignments, importLeadAssignments } from "../services/leadAssignmentService";
import { isSuperAdmin, isAdmin } from "../utils/role";
import {
  useLeadAssignments, useAllEmployees, useSaveRoster,
  useRemoveFromService, useClearService,
} from "../hooks/useLeadAssignments";

/**
 * Lead Assigned Master — which employees receive which service's leads, and how
 * many each may take in the quota period.
 *
 * Quota has three distinct states and they must not blur into each other:
 *   null → unlimited      0 → paused (takes nothing)      >0 → a cap
 * An empty input means unlimited; a typed 0 means paused.
 *
 * Saving is per service and replaces that service's whole roster — so add, edit
 * and reorder are all one call.
 */

const card = {
  background: "white", borderRadius: 16, border: "1px solid #f0f0f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden", marginBottom: 18,
};
const cardHead = {
  padding: "14px 20px", borderBottom: "1px solid #f0f0f0", background: "#fafbff",
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
};
const th = {
  padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap",
};
const td = { padding: "11px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" };
const fieldLabel = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };

const isOn = (v) => v === 1 || v === true || v === "1";
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

/** Present, or absent with the date the absence runs to. */
const AttendancePill = ({ employee }) => {
  const absent = isOn(employee.is_absent_today);
  return (
    <span
      style={{
        background: absent ? "#fef2f2" : "#f0fdf4",
        color: absent ? "#dc2626" : "#16a34a",
        border: `1px solid ${absent ? "#fecaca" : "#bbf7d0"}`,
        borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
      }}
    >
      {absent
        ? `Absent${employee.absent_until ? ` till ${fmtDate(employee.absent_until)}` : ""}`
        : "Present"}
    </span>
  );
};

/**
 * Quota editor.
 *
 * The state is picked explicitly instead of being inferred from an empty box.
 * In the spreadsheet a blank cell means "not assigned at all", so letting a
 * blank field mean "unlimited" here would have taught two different rules for
 * the same thing. Removing an employee is the delete button, in both places.
 */
const QuotaEditor = ({ quota, onChange }) => {
  const mode = quota === null || quota === undefined ? "unlimited"
    : Number(quota) === 0 ? "paused" : "limit";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <Select
        value={mode}
        onChange={(m) => onChange(m === "unlimited" ? null : m === "paused" ? 0 : 1)}
        style={{ width: 132 }}
        options={[
          { value: "limit", label: "Limit" },
          { value: "unlimited", label: "Unlimited" },
          { value: "paused", label: "Paused" },
        ]}
      />
      {mode === "limit" && (
        <InputNumber
          value={quota}
          // 1 is the smallest real limit; 0 is a separate state above.
          onChange={(v) => onChange(v === null || v === undefined ? 1 : v)}
          min={1}
          style={{ width: 96 }}
        />
      )}
      {mode === "unlimited" && (
        <span style={{ fontSize: 11.5, color: "#16a34a", fontWeight: 700 }}>no cap</span>
      )}
      {mode === "paused" && (
        <span style={{ fontSize: 11.5, color: "#b45309", fontWeight: 700 }}>takes no leads</span>
      )}
    </div>
  );
};

/** Read-only rendering of the same three states. */
const quotaText = (quota) =>
  quota === null || quota === undefined ? "Unlimited" : Number(quota) === 0 ? "Paused" : quota;

/* The overall cap uses a different convention from the per-service quota: here 0
   means unlimited, because that is what "no lead limit set" has always meant on
   the employee record. */
const overallLimitText = (limit) =>
  limit === null || limit === undefined || Number(limit) === 0
    ? "Unlimited"
    : Number(limit).toLocaleString("en-IN");

/**
 * Import from a spreadsheet.
 *
 * The grid is the full state: every service row in the file has its roster
 * replaced, and a blank cell means "not assigned". That is why exporting first
 * is not a suggestion here but the safe path — it is spelled out on the screen.
 */
const ImportModal = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [createMissing, setCreateMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState(null);
  const inputRef = useRef(null);

  const downloadFormat = async () => {
    setDownloading(true);
    try {
      await exportLeadAssignments();
    } catch (err) {
      toast.error(err?.message || "The download failed");
    } finally {
      setDownloading(false);
    }
  };

  const submit = async () => {
    if (!file) return toast.error("Choose a file first");
    setBusy(true);
    try {
      const result = await importLeadAssignments(file, createMissing);
      setSummary(result);
      onImported();
    } catch (err) {
      toast.error(err?.message || "The file could not be imported");
    } finally {
      setBusy(false);
    }
  };

  const box = { border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px" };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>Import lead assignments</span>}
      onCancel={onClose}
      width={640}
      footer={
        summary ? (
          <button
            onClick={onClose}
            style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: "#0d9488", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
          >
            Done
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={onClose}
              disabled={busy}
              style={{ padding: "7px 18px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy || !file}
              style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: file ? "#0d9488" : "#d1d5db", color: "#fff", fontWeight: 700, fontSize: 13, cursor: busy || !file ? "not-allowed" : "pointer" }}
            >
              {busy ? "Importing…" : "Save"}
            </button>
          </div>
        )
      }
    >
      {summary ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4 }}>
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "11px 14px", fontSize: 13, color: "#16a34a", fontWeight: 600 }}>
            {summary.quotas_set ?? 0} quotas set across {summary.rosters_updated ?? 0}{" "}
            {summary.rosters_updated === 1 ? "service" : "services"}
            {summary.services_created > 0 && ` · ${summary.services_created} new service(s) created`}
          </div>

          {(summary.skipped_services?.length > 0 || summary.skipped_employees?.length > 0) && (
            <div style={{ ...box, borderColor: "#fde68a", background: "#fffbeb" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
                Skipped — not found in the database
              </div>
              {summary.skipped_services?.length > 0 && (
                <div style={{ fontSize: 12, color: "#92400e" }}>
                  Services: {summary.skipped_services.join(", ")}
                </div>
              )}
              {summary.skipped_employees?.length > 0 && (
                <div style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}>
                  Employees: {summary.skipped_employees.join(", ")}
                </div>
              )}
            </div>
          )}

          {summary.errors?.length > 0 && (
            <div style={{ ...box, borderColor: "#fecaca", background: "#fef2f2", padding: 0, overflow: "hidden" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#991b1b", padding: "10px 14px" }}>
                {summary.errors.length} cell{summary.errors.length === 1 ? "" : "s"} could not be read
              </div>
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead style={{ background: "#fff1f2" }}>
                    <tr>
                      <th style={{ ...th, padding: "8px 14px" }}>Row</th>
                      <th style={{ ...th, padding: "8px 14px" }}>Column</th>
                      <th style={{ ...th, padding: "8px 14px" }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.errors.map((e, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #fee2e2" }}>
                        <td style={{ padding: "8px 14px", color: "#991b1b" }}>{e.row}</td>
                        <td style={{ padding: "8px 14px", color: "#991b1b", wordBreak: "break-all" }}>{e.column}</td>
                        <td style={{ padding: "8px 14px", color: "#7f1d1d" }}>{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4 }}>
          <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, padding: "13px 14px", fontSize: 12.5, color: "#0f766e", lineHeight: 1.6 }}>
            The sheet is the full state — importing replaces each listed service's roster, and a
            blank cell removes that employee from it. So start from the current data rather than
            a fresh file.
            <div style={{ marginTop: 8 }}>
              <b>Cells:</b> a number = monthly quota · <code>U</code> = unlimited ·{" "}
              <code>0</code> or blank = not assigned.
            </div>
            {/* The safe path needs to be one click away, not an instruction. */}
            <button
              onClick={downloadFormat}
              disabled={downloading}
              style={{ marginTop: 11, display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 16px", borderRadius: 8, border: "none", background: "#0d9488", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: downloading ? "not-allowed" : "pointer" }}
            >
              <DownloadOutlined /> {downloading ? "Preparing…" : "Download current data (.xlsx)"}
            </button>
          </div>

          <div>
            <label style={fieldLabel}>Choose file</label>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={{ fontSize: 13 }}
            />
            {file && (
              <div style={{ fontSize: 12, color: "#0d9488", marginTop: 6, fontWeight: 600 }}>{file.name}</div>
            )}
          </div>

          <label style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={createMissing}
              onChange={(e) => setCreateMissing(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Create missing services</span>
              <div style={{ fontSize: 11.5, color: "#b45309", marginTop: 3, lineHeight: 1.5 }}>
                Leave this off unless you mean it — a typo in a service name would create a new
                junk service instead of being skipped.
              </div>
            </span>
          </label>
        </div>
      )}
    </Modal>
  );
};

/**
 * Assign in bulk, from the top of the page.
 *
 * Going service by service is fine for one change and painful for onboarding a
 * team: five people across four services is twenty clicks through four cards.
 * This picks both sides at once and writes one roster per service.
 *
 * "Add" is the default because it is the safe one. "Replace" is what a rebuild
 * needs, and it says out loud how many people it would drop.
 */
const BulkAssignModal = ({ services, employees, onClose, onApply, saving }) => {
  const [serviceIds, setServiceIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [quota, setQuota] = useState(null);          // null = unlimited
  const [mode, setMode] = useState("add");           // "add" | "replace"
  const [error, setError] = useState("");

  const chosen = services.filter((s) => serviceIds.includes(s.service_id));

  /* What this would actually change, counted before anything is sent — replace
     can quietly unassign people. */
  const preview = (() => {
    let added = 0;
    let removed = 0;
    chosen.forEach((service) => {
      const current = (service.employees || []).map((e) => e.user_id);
      added += userIds.filter((id) => !current.includes(id)).length;
      if (mode === "replace") removed += current.filter((id) => !userIds.includes(id)).length;
    });
    return { added, removed };
  })();

  const submit = () => {
    if (!serviceIds.length) return setError("Pick at least one service");
    if (!userIds.length) return setError("Pick at least one employee");
    setError("");
    onApply({ serviceIds, userIds, quota, mode });
  };

  const modeBtn = (key, title, sub) => (
    <button
      key={key}
      onClick={() => setMode(key)}
      style={{
        textAlign: "left", padding: "11px 13px", borderRadius: 10, cursor: "pointer",
        border: `1.5px solid ${mode === key ? "#0d9488" : "#e5e7eb"}`,
        background: mode === key ? "#f0fdfa" : "#fff",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: mode === key ? "#0f766e" : "#111827" }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>{sub}</div>
    </button>
  );

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>Assign employees to services</span>}
      onCancel={onClose}
      onOk={submit}
      okText={saving ? "Assigning…" : "Assign"}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={620}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <label style={fieldLabel}>Services <span style={{ color: "#dc2626" }}>*</span></label>
          <Select
            mode="multiple" value={serviceIds} onChange={(v) => { setServiceIds(v); setError(""); }}
            placeholder="Pick one or more services" style={{ width: "100%" }}
            optionFilterProp="label"
            options={services.map((s) => ({ value: s.service_id, label: s.service_name }))}
          />
        </div>

        <div>
          <label style={fieldLabel}>Employees <span style={{ color: "#dc2626" }}>*</span></label>
          <Select
            mode="multiple" value={userIds} onChange={(v) => { setUserIds(v); setError(""); }}
            placeholder="Pick one or more employees" style={{ width: "100%" }}
            optionFilterProp="label"
            options={employees.map((m) => ({ value: m.id, label: m.name }))}
          />
        </div>

        <div>
          <label style={fieldLabel}>Quota for everyone picked</label>
          <QuotaEditor quota={quota} onChange={setQuota} />
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 6 }}>
            Applied to every pair created here. A single person can be fine-tuned afterwards on
            their own service.
          </div>
        </div>

        <div>
          <label style={fieldLabel}>How to apply</label>
          <div style={{ display: "grid", gap: 8 }}>
            {modeBtn("add", "Add to the existing roster", "Everyone already on these services stays")}
            {modeBtn("replace", "Replace the roster", "These services end up with exactly the employees picked above")}
          </div>
        </div>

        {(preview.added > 0 || preview.removed > 0) && (
          <div style={{ background: preview.removed ? "#fffbeb" : "#f0fdfa", border: `1px solid ${preview.removed ? "#fde68a" : "#99f6e4"}`, borderRadius: 10, padding: "10px 13px", fontSize: 12.5, color: preview.removed ? "#92400e" : "#0f766e", lineHeight: 1.6 }}>
            {preview.added > 0 && (
              <div>
                {preview.added} assignment{preview.added === 1 ? "" : "s"} will be created across{" "}
                {chosen.length} service{chosen.length === 1 ? "" : "s"}.
              </div>
            )}
            {preview.removed > 0 && (
              <div>
                <b>{preview.removed}</b> existing assignment{preview.removed === 1 ? "" : "s"} will be removed.
              </div>
            )}
          </div>
        )}

        {error && <div style={{ color: "#dc2626", fontSize: 12.5 }}>{error}</div>}
      </div>
    </Modal>
  );
};

const ManageLeadAssignment = () => {
  const [serviceFilter, setServiceFilter] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState(null);
  const [search, setSearch] = useState("");
  /* Only the services being edited live here; everything else renders straight
     from the server. */
  const [edits, setEdits] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  /* With a handful of services everything is open; past that they collapse, or
     the page becomes one endless scroll. */
  const [collapsed, setCollapsed] = useState({});
  // Long rosters render a slice until asked for the rest.
  const [showAll, setShowAll] = useState({});

  const filters = useMemo(
    () => ({
      ...(serviceFilter ? { service_id: serviceFilter } : {}),
      ...(employeeFilter ? { user_id: employeeFilter } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [serviceFilter, employeeFilter, search],
  );

  const { data, isLoading, isFetching, refetch } = useLeadAssignments(filters);
  const { data: employees = [] } = useAllEmployees();
  const saveRoster = useSaveRoster();
  const removeOne = useRemoveFromService();
  const clearOne = useClearService();

  const services = data?.services || [];
  const quotaPeriod = data?.quotaPeriod || "monthly";
  const periodLabel = quotaPeriod === "daily" ? "per day"
    : quotaPeriod === "lifetime" ? "total" : "per month";
  const usedLabel = quotaPeriod === "daily" ? "Used today"
    : quotaPeriod === "lifetime" ? "Used" : "Used this month";

  const mayManage = isSuperAdmin() || isAdmin();

  const COLLAPSE_ABOVE = 6;      // services on screen before they fold up
  const ROSTER_PREVIEW = 25;     // rows shown before "show all"
  const autoCollapse = services.length > COLLAPSE_ABOVE;
  /* Explicit state wins; otherwise it follows how many services there are. A
     service being edited always stays open. */
  const isOpen = (id) => {
    // Unsaved edits are never hidden — they would be saved unseen otherwise.
    if (edits[id]) return true;
    return collapsed[id] !== undefined ? !collapsed[id] : !autoCollapse;
  };
  const toggleOpen = (id) => setCollapsed((p) => ({ ...p, [id]: isOpen(id) }));

  /** The rows on screen for a service: its edits if any, else what the API sent. */
  const rowsFor = (service) =>
    edits[service.service_id] ||
    (service.employees || []).map((e) => ({ user_id: e.user_id, quota: e.quota, _saved: e }));

  const isDirty = (serviceId) => Boolean(edits[serviceId]);

  const startEdit = (service, mutate) =>
    setEdits((prev) => ({ ...prev, [service.service_id]: mutate(rowsFor(service)) }));

  const cancelEdit = (serviceId) =>
    setEdits((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });

  const save = (service) => {
    const rows = rowsFor(service).filter((r) => r.user_id);
    saveRoster.mutate(
      {
        serviceId: service.service_id,
        employees: rows.map((r) => ({
          user_id: r.user_id,
          // Empty means unlimited; a typed 0 means paused. Both must survive.
          quota: r.quota === "" || r.quota === undefined ? null : r.quota,
        })),
      },
      { onSuccess: () => cancelEdit(service.service_id) },
    );
  };

  const removeEmployee = (service, row) => {
    // A row that was only just added has nothing on the server to delete.
    if (!row._saved) {
      startEdit(service, (rows) => rows.filter((r) => r !== row));
      return;
    }
    Swal.fire({
      title: `Remove ${row._saved.name}?`,
      html: `They will stop receiving new <b>${service.service_name}</b> leads. Their other services are untouched.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, remove",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      cancelEdit(service.service_id);
      removeOne.mutate({ serviceId: service.service_id, userId: row.user_id });
    });
  };

  const clearService = (service) => {
    Swal.fire({
      title: `Clear "${service.service_name}"?`,
      html: `All <b>${service.employees?.length || 0}</b> employees will stop receiving leads from this service, and new ones will stay unassigned.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, clear",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      cancelEdit(service.service_id);
      clearOne.mutate({ serviceId: service.service_id });
    });
  };

  /* One roster write per selected service — the same call the per-service Save
     uses, so bulk and single-service edits cannot drift apart. */
  const applyBulk = async ({ serviceIds, userIds, quota, mode }) => {
    setBulkSaving(true);
    try {
      await Promise.all(
        serviceIds.map((serviceId) => {
          const service = services.find((s) => s.service_id === serviceId);
          const current = service?.employees || [];
          // "add" keeps whoever is not being re-specified; "replace" keeps nobody.
          const kept = mode === "replace"
            ? []
            : current
                .filter((e) => !userIds.includes(e.user_id))
                .map((e) => ({ user_id: e.user_id, quota: e.quota }));
          const picked = userIds.map((user_id) => ({ user_id, quota }));
          return saveRoster.mutateAsync({ serviceId, employees: [...kept, ...picked] });
        }),
      );
      setBulkOpen(false);
    } catch {
      // The mutation has already surfaced the API's own reason.
    } finally {
      setBulkSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportLeadAssignments();
    } catch (err) {
      toast.error(err?.message || "The export failed");
    } finally {
      setExporting(false);
    }
  };

  const btn = {
    padding: "6px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
    border: "1px solid #99f6e4", background: "#f0fdfa", color: "#0d9488",
    display: "inline-flex", alignItems: "center", gap: 6,
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#0d9488 0%,#0f766e 100%)", borderRadius: 16, padding: "26px 30px", marginBottom: 20, boxShadow: "0 8px 32px rgba(13,148,136,0.25)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 54, height: 54, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "white" }}>
                <ApartmentOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 23, fontWeight: 700 }}>Lead Assigned Master</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>
                  Which employees receive which service's leads
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Tooltip title="Refresh">
                <button
                  onClick={() => refetch()}
                  style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
                >
                  <ReloadOutlined spin={isFetching} />
                </button>
              </Tooltip>
              <button
                onClick={handleExport}
                disabled={exporting}
                style={{ display: "flex", alignItems: "center", gap: 7, height: 40, padding: "0 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.12)", color: "white", fontWeight: 700, fontSize: 13.5, cursor: exporting ? "not-allowed" : "pointer" }}
              >
                <DownloadOutlined /> {exporting ? "Exporting…" : "Export"}
              </button>
              {mayManage && (
                <button
                  onClick={() => setBulkOpen(true)}
                  style={{ display: "flex", alignItems: "center", gap: 7, height: 40, padding: "0 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.12)", color: "white", fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}
                >
                  <PlusOutlined /> Assign employees
                </button>
              )}
              {mayManage && (
                <button
                  onClick={() => setImporting(true)}
                  style={{ display: "flex", alignItems: "center", gap: 7, height: 40, padding: "0 18px", borderRadius: 10, border: "none", background: "white", color: "#0f766e", fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}
                >
                  <UploadOutlined /> Import Data
                </button>
              )}
            </div>
          </div>

          {!mayManage && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "10px 16px", marginBottom: 18, fontSize: 13, color: "#92400e" }}>
              You can read the assignments, but only an admin or superadmin can change them.
            </div>
          )}

          {/* Filters */}
          <div style={{ ...card, marginBottom: 20 }}>
            <div style={{ padding: "16px 20px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ minWidth: 220 }}>
                <label style={fieldLabel}>Service Name</label>
                <Select
                  value={serviceFilter} onChange={setServiceFilter} allowClear
                  placeholder="All services" style={{ width: "100%" }}
                  showSearch optionFilterProp="label"
                  options={services.map((s) => ({ value: s.service_id, label: s.service_name }))}
                />
              </div>
              <div style={{ minWidth: 220 }}>
                <label style={fieldLabel}>Employee</label>
                <Select
                  value={employeeFilter} onChange={setEmployeeFilter} allowClear
                  placeholder="All employees" style={{ width: "100%" }}
                  showSearch optionFilterProp="label"
                  options={employees.map((m) => ({ value: m.id, label: m.name }))}
                />
              </div>
              <div style={{ minWidth: 240, flex: 1 }}>
                <label style={fieldLabel}>Search</label>
                <Input
                  value={search} onChange={(e) => setSearch(e.target.value)} allowClear
                  placeholder="Service or employee name…"
                  prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div style={{ ...card, padding: 24 }}><Skeleton active paragraph={{ rows: 6 }} /></div>
          ) : services.length === 0 ? (
            <div style={{ ...card, padding: "48px 20px" }}>
              <Empty description="No service matches these filters." />
            </div>
          ) : (
            services.map((service) => {
              const rows = rowsFor(service);
              const dirty = isDirty(service.service_id);
              const taken = rows.map((r) => r.user_id).filter(Boolean);
              const open = isOpen(service.service_id);
              const expanded = showAll[service.service_id];
              /* Editing shows the whole roster: a hidden row would still be sent
                 on save, and it must be visible to be trusted. */
              const visibleRows = expanded || dirty ? rows : rows.slice(0, ROSTER_PREVIEW);
              const hiddenCount = rows.length - visibleRows.length;

              return (
                <div key={service.service_id} style={card}>
                  <div style={cardHead}>
                    {/* The whole title toggles the section — with 50 services the
                        page is unusable if they are all expanded. */}
                    <button
                      onClick={() => toggleOpen(service.service_id)}
                      style={{ border: "none", background: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 9 }}
                    >
                      <span style={{ color: "#9ca3af", fontSize: 11, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>
                        <RightOutlined />
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                        {service.service_name}
                      </span>
                    </button>
                    <span style={{ fontFamily: "monospace", fontSize: 11.5, background: "#f0fdfa", color: "#0d9488", border: "1px solid #99f6e4", borderRadius: 6, padding: "2px 8px" }}>
                      {service.service_slug}
                    </span>
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>
                      {rows.length} {rows.length === 1 ? "employee" : "employees"}
                    </span>

                    {mayManage && (
                      <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {dirty ? (
                          <>
                            <button
                              style={{ ...btn, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280" }}
                              onClick={() => cancelEdit(service.service_id)}
                              disabled={saveRoster.isPending}
                            >
                              Cancel
                            </button>
                            <button
                              style={{ ...btn, border: "none", background: "#0d9488", color: "#fff" }}
                              onClick={() => save(service)}
                              disabled={saveRoster.isPending}
                            >
                              <SaveOutlined /> {saveRoster.isPending ? "Saving…" : "Save changes"}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              style={btn}
                              onClick={() => startEdit(service, (r) => [...r, { user_id: null, quota: null }])}
                            >
                              <PlusOutlined /> Add employee
                            </button>
                            {rows.length > 0 && (
                              <button
                                style={{ ...btn, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626" }}
                                onClick={() => clearService(service)}
                                disabled={clearOne.isPending}
                              >
                                <DeleteOutlined /> Clear service
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {!open ? null : rows.length === 0 ? (
                    <div style={{ padding: "26px 20px", textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
                      Nobody is assigned — leads from this service will stay unassigned.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead style={{ background: "#fafbff" }}>
                          <tr>
                            <th style={th}>Employee</th>
                            <th style={th}>Email</th>
                            <th style={th}>Attendance</th>
                            <th style={th}>Quota <span style={{ textTransform: "none", fontWeight: 500, color: "#9ca3af" }}>({periodLabel})</span></th>
                            <th style={th}>{usedLabel}</th>
                            <th style={th}>
                              <Tooltip title="Overall lifetime limit — edit on the employee profile">
                                <span style={{ borderBottom: "1px dotted #cbd5e1", cursor: "help" }}>Overall</span>
                              </Tooltip>
                            </th>
                            <th style={th}>Used overall</th>
                            {mayManage && <th style={{ ...th, textAlign: "center", width: 80 }}>—</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {visibleRows.map((row, i) => {
                            const saved = row._saved;
                            const picked = employees.find((m) => m.id === row.user_id);
                            /* A newly picked row has no server record yet, so the
                               employee list stands in for the overall figures. */
                            const overallSource = saved || picked;
                            const quotaFull =
                              saved && saved.quota !== null && saved.quota > 0 &&
                              saved.assigned_count >= saved.quota;
                            return (
                              <tr key={saved?.id || `new-${i}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                                <td style={td}>
                                  {saved ? (
                                    <span style={{ fontWeight: 700, color: "#111827" }}>{saved.name}</span>
                                  ) : (
                                    <Select
                                      value={row.user_id}
                                      onChange={(v) =>
                                        startEdit(service, (rs) =>
                                          rs.map((r) => (r === row ? { ...r, user_id: v } : r)))}
                                      placeholder="Select an employee"
                                      style={{ minWidth: 210 }}
                                      showSearch optionFilterProp="label"
                                      options={employees.map((m) => ({
                                        value: m.id,
                                        label: m.name,
                                        // Nobody twice on the same service.
                                        disabled: taken.includes(m.id) && row.user_id !== m.id,
                                      }))}
                                    />
                                  )}
                                </td>
                                <td style={{ ...td, color: "#6b7280" }}>
                                  {saved?.email || picked?.email || <span style={{ color: "#d1d5db" }}>—</span>}
                                </td>
                                <td style={td}>
                                  {/* A row picked but not yet saved has no server
                                      record — the employee list already carries
                                      the same attendance fields, so it fills in. */}
                                  {saved || picked ? (
                                    <AttendancePill employee={saved || picked} />
                                  ) : (
                                    <span style={{ color: "#d1d5db" }}>—</span>
                                  )}
                                </td>
                                <td style={td}>
                                  {mayManage ? (
                                    <QuotaEditor
                                      quota={row.quota}
                                      onChange={(v) =>
                                        startEdit(service, (rs) =>
                                          rs.map((r) => (r === row ? { ...r, quota: v } : r)))}
                                    />
                                  ) : (
                                    <span>{quotaText(row.quota)}</span>
                                  )}
                                </td>
                                <td style={td}>
                                  {saved ? (
                                    <span style={{ fontWeight: 600, color: quotaFull ? "#b45309" : "#374151", fontVariantNumeric: "tabular-nums" }}>
                                      {saved.assigned_count ?? 0} /{" "}
                                      {saved.quota === null || saved.quota === undefined ? "∞" : saved.quota}
                                      {quotaFull && (
                                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700 }}>full</span>
                                      )}
                                    </span>
                                  ) : (
                                    /* Nothing has been counted against a service
                                       this employee is not on yet. */
                                    <span style={{ fontSize: 11.5, color: "#9ca3af" }}>after save</span>
                                  )}
                                </td>

                                {/* The lifetime cap belongs to the employee, not
                                    to this service — shown here, edited on the
                                    employee profile. */}
                                <td style={td}>
                                  {overallSource ? (
                                    <Tooltip title="Overall lifetime limit — edit on the employee profile">
                                      <span style={{ color: "#6b7280", cursor: "help" }}>
                                        {overallLimitText(overallSource.overall_limit ?? overallSource.lead_limit)}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span style={{ color: "#d1d5db" }}>—</span>
                                  )}
                                </td>
                                <td style={td}>
                                  {saved ? (
                                    <span style={{ color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                                      {saved.overall_used ?? 0} /{" "}
                                      {overallLimitText(saved.overall_limit)}
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: 11.5, color: "#9ca3af" }}>after save</span>
                                  )}
                                </td>

                                {mayManage && (
                                  <td style={{ ...td, textAlign: "center" }}>
                                    <Tooltip title={saved ? "Remove from this service" : "Discard this row"}>
                                      <button
                                        onClick={() => removeEmployee(service, row)}
                                        disabled={removeOne.isPending}
                                        style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: removeOne.isPending ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}
                                      >
                                        <DeleteOutlined />
                                      </button>
                                    </Tooltip>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {open && hiddenCount > 0 && (
                    <div style={{ padding: "11px 20px", borderTop: "1px solid #f1f5f9", textAlign: "center", background: "#fafbff" }}>
                      <button
                        onClick={() => setShowAll((p) => ({ ...p, [service.service_id]: true }))}
                        style={{ border: "none", background: "none", color: "#0d9488", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                      >
                        Show all {rows.length} employees ({hiddenCount} more)
                      </button>
                    </div>
                  )}

                  {open && dirty && (
                    <div style={{ padding: "10px 20px", borderTop: "1px solid #f1f5f9", fontSize: 11.5, color: "#b45309", background: "#fffbeb" }}>
                      Unsaved changes — anyone removed from this list loses this service when you save.
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {bulkOpen && (
        <BulkAssignModal
          services={services}
          employees={employees}
          saving={bulkSaving}
          onClose={() => setBulkOpen(false)}
          onApply={applyBulk}
        />
      )}

      {importing && (
        <ImportModal
          onClose={() => setImporting(false)}
          onImported={() => refetch()}
        />
      )}
    </div>
  );
};

export default ManageLeadAssignment;
